import {
    Dialog,
    DialogContent,
    DialogClose,
} from '@/components/ui/dialog'
import { Check, Store, ArrowRight, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/hooks/usePermission'
import { PERMISSIONS } from '@/features/auth/constants'

interface ConnectStoreModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onConnectClick?: () => void
}

const BENEFITS = [
    {
        title: 'Post-purchase surveys',
        desc: 'Collect feedback right after checkout on the order status page',
    },
    {
        title: 'On-site popups & widgets',
        desc: 'Engage visitors with beautifully targeted surveys',
    },
    {
        title: 'Link & QR code surveys',
        desc: 'Share surveys anywhere via links or scannable QR codes',
    },
]

export default function ConnectStoreModal({
    open,
    onOpenChange,
    onConnectClick
}: ConnectStoreModalProps) {
    const navigate = useNavigate()
    const canManageIntegrations = usePermission(PERMISSIONS.INTEGRATIONS_WRITE)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[500px] p-0 gap-0 bg-[#FFFFFF] rounded-[20px] shadow-lg border-none overflow-hidden"
                hideClose
            >
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-[10px] bg-[#F4F4F4] flex items-center justify-center">
                            <Store className="w-5 h-5 text-[#303030]" strokeWidth={1.5} />
                        </div>
                        <div>
                            <h2 className="text-[20px] font-[600] text-[#303030]">
                                Connect your Shopify store
                            </h2>
                            <p className="text-[14px] font-[400] text-[#656565] mt-[2px]">
                                Link your store to unlock all survey channels.
                            </p>
                        </div>
                    </div>
                    <DialogClose className="p-2 -mr-2 -mt-2 rounded-full transition-colors hover:bg-gray-100">
                        <X className="w-5 h-5" stroke="#8A8A8A" />
                    </DialogClose>
                </div>

                {/* Benefits */}
                <div className="px-6 py-4">
                    <p className="text-[12px] font-[600] text-[#8A8A8A] uppercase tracking-wider mb-4">
                        What you'll unlock
                    </p>
                    <ul className="space-y-4">
                        {BENEFITS.map((b) => (
                            <li key={b.title} className="flex items-start gap-4">
                                <div className="mt-0.5 w-[20px] h-[20px] rounded-full bg-[#E5F5E5] flex flex-shrink-0 items-center justify-center">
                                    <Check className="text-[#2E8B57] w-3 h-3" strokeWidth={3} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-[600] text-[15px] text-[#303030]">
                                        {b.title}
                                    </span>
                                    <span className="text-[14px] font-[400] text-[#656565] mt-0.5">
                                        {b.desc}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Footer */}
                <div className="px-6 py-5 flex items-center justify-end gap-3 rounded-b-[20px] border-t border-gray-100 mt-2">
                    <button
                        onClick={() => onOpenChange(false)}
                        className="px-5 py-[10px] text-[15px] font-[600] text-[#656565] border border-[#E5E5E5] hover:bg-[#F4F4F4] hover:border-[#D0D0D0] rounded-[10px] transition-colors"
                    >
                        {canManageIntegrations ? 'Maybe later' : 'Close'}
                    </button>
                    {canManageIntegrations ? (
                        <button
                            onClick={() => {
                                if (onConnectClick) {
                                    onConnectClick()
                                }
                                onOpenChange(false)
                                navigate('/settings/integrations')
                            }}
                            className="group inline-flex items-center gap-2 px-6 py-[10px] text-[15px] font-[600] text-white bg-[#303030] hover:bg-[#1A1A1A] rounded-[10px] transition-colors"
                        >
                            Connect Store
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" stroke="#FFFFFF" />
                        </button>
                    ) : (
                        <div className="px-4 py-2 bg-amber-50 text-amber-700 text-[13px] font-[500] rounded-lg border border-amber-200">
                            You don't have permission to connect stores. Please contact your administrator.
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
